/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import msg from '@salesforce/retail-react-app/app/components/display-price/messages'

// Custom Contexts
import {useConversionRate} from '../../custom-contexts'

// Custom hooks
import useShipToCountry from '../../hooks/use-ship-to-country'

// utils
import {formatPrice} from '../../utils/price-utils'

/**
 * Component that displays current price of a product with a11y
 * @param price - price of the product
 * @param as - an HTML tag or component to be rendered as
 * @param isRange - show price as range or not
 * @param currency - currency to show the price in
 * @param labelForA11y - label to be used for a11y
 * @param extraProps - extra props to be passed into Text Component
 * @returns {JSX.Element}
 */
const CurrentPrice = ({
    labelForA11y,
    price,
    as,
    isRange = false,
    currency,
    conversionsRate,
    shipToCountryCode,
    ...extraProps
}) => {
    const contextConversionsRate = useConversionRate()
    const {shipToCountry: contextShipToCountry} = useShipToCountry()
    const intl = useIntl()

    const priceConversionsRate = conversionsRate || contextConversionsRate
    const shipToCountryId = shipToCountryCode || contextShipToCountry?.id

    const currentPriceText = formatPrice(
        price,
        currency,
        priceConversionsRate,
        shipToCountryId,
        intl
    )

    return (
        <>
            {isRange ? (
                <Text
                    as={as}
                    {...extraProps}
                    aria-live={'polite'}
                    aria-label={intl.formatMessage(msg.ariaLabelCurrentPriceWithRange, {
                        currentPrice: currentPriceText
                    })}
                >
                    {intl.formatMessage(msg.currentPriceWithRange, {
                        currentPrice: currentPriceText
                    })}
                </Text>
            ) : (
                <Text
                    as={as}
                    {...extraProps}
                    aria-label={intl.formatMessage(msg.ariaLabelCurrentPrice, {
                        currentPrice: currentPriceText
                    })}
                >
                    {currentPriceText}
                </Text>
            )}
        </>
    )
}
CurrentPrice.propTypes = {
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string,
    labelForA11y: PropTypes.string,
    as: PropTypes.string,
    isRange: PropTypes.bool,
    extraProps: PropTypes.object
}

export default CurrentPrice
